import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import api, { LOGOS, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader2, XCircle, Eye, EyeOff, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function Activate() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const token = params.get("token");
  const [state, setState] = useState("loading");
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!token) { setState("error"); setError("This link is missing its activation code."); return; }
    api.get(`/auth/activation?token=${encodeURIComponent(token)}`)
      .then(({ data }) => { setInfo(data); setState("form"); })
      .catch((e) => { setState("error"); setError(formatApiError(e.response?.data?.detail)); });
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) return toast.error("Password must be at least 8 characters.");
    if (pw !== pw2) return toast.error("The two passwords don't match.");
    setSaving(true);
    try {
      const { data } = await api.post("/auth/activate", { token, password: pw });
      if (data.token) localStorage.setItem("cc_token", data.token);
      setUser(data.user);
      toast.success("Your account is active. Welcome to Campus Connections!");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA] p-6">
      <div className="w-full max-w-md bg-white border rounded-xl p-8" data-testid="activate-card">
        <img src={LOGOS.campus} alt="Campus Connections" className="h-14 w-auto mx-auto mb-6 object-contain" />

        {state === "loading" && (
          <div className="text-center py-6"><Loader2 className="w-10 h-10 mx-auto text-cc-blue animate-spin" /></div>
        )}

        {state === "error" && (
          <div className="text-center">
            <XCircle className="w-12 h-12 mx-auto text-red-500" />
            <p className="mt-4 text-zinc-700" data-testid="activate-error">{error}</p>
            <Link to="/login" className="inline-block mt-6 px-5 py-2 rounded-md bg-cc-blue text-white text-sm font-medium">Go to sign in</Link>
          </div>
        )}

        {state === "form" && (
          <form onSubmit={submit} className="space-y-4" data-testid="activate-form">
            <div className="text-center">
              <h1 className="text-2xl font-semibold text-zinc-900">Welcome, {(info?.name || "").split(" ")[0]}</h1>
              <p className="mt-1 text-sm text-zinc-500">
                Choose a password to activate <span className="font-medium text-zinc-700" data-testid="activate-email">{info?.email}</span>
                {info?.role ? <> as a <span className="font-medium text-zinc-700">{info.role}</span></> : null}.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-1">Create a password</label>
              <div className="relative">
                <input type={show ? "text" : "password"} value={pw} onChange={(e) => setPw(e.target.value)} autoFocus
                  className="w-full border rounded-md px-3 py-2 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-cc-blue/40"
                  placeholder="At least 8 characters" data-testid="activate-password" />
                <button type="button" onClick={() => setShow(!show)} className="absolute right-2 top-2.5 text-zinc-400" data-testid="activate-toggle-visibility">
                  {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-1">Confirm password</label>
              <input type={show ? "text" : "password"} value={pw2} onChange={(e) => setPw2(e.target.value)}
                className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cc-blue/40"
                placeholder="Re-enter your password" data-testid="activate-password-confirm" />
            </div>
            <button type="submit" disabled={saving}
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-md bg-cc-blue text-white text-sm font-semibold disabled:opacity-60"
              data-testid="activate-submit">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
              {saving ? "Activating…" : "Activate & sign in"}
            </button>
            <p className="text-xs text-center text-zinc-400">You'll be taken straight to your dashboard.</p>
          </form>
        )}
      </div>
    </div>
  );
}
